import { useBoolean } from '@chakra-ui/react';
import { createContext, useContext, ReactNode, useCallback, useState } from 'react';
import { useFocus } from '../Focus';

import { ContextMenuContextI, ContextMenuItem } from './ContextMenu.types';

const defaultContext = {
  items: [],
  setItems: () => {},
  isOpen: false,
  position: null,
  openMenu: () => {},
  hideMenu: () => {},
};
const ContextMenuContext = createContext<ContextMenuContextI>(defaultContext);

export const ContextMenuProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<ContextMenuItem[]>([]);
  const [isOpen, setIsOpen] = useBoolean(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

  const { pageScrollContainerRef } = useFocus();

  /**
   * Open menu at specified position
   * Position is relative to window, this function will convert to
   */
  const openMenu = useCallback(
    (pos: { x: number; y: number }) => {
      const pageScrollContainer = pageScrollContainerRef?.current;
      const targetPageRect = pageScrollContainer?.getBoundingClientRect() ?? { x: 0, y: 0 };

      const scrollTop = pageScrollContainer?.scrollTop ?? 0;
      const scrollLeft = pageScrollContainer?.scrollLeft ?? 0;

      setPosition({
        x: pos.x - targetPageRect.x + scrollLeft,
        y: pos.y - targetPageRect.y + scrollTop,
      });
      setIsOpen.on();
    },
    [setIsOpen, pageScrollContainerRef],
  );

  const hideMenu = useCallback(() => {
    if (!isOpen) return;
    setPosition(null);
    setIsOpen.off();
  }, [setIsOpen, isOpen]);

  return (
    <ContextMenuContext.Provider
      value={{
        items,
        setItems,
        isOpen,
        position,
        openMenu,
        hideMenu,
      }}
    >
      {children}
    </ContextMenuContext.Provider>
  );
};

/**
 *  Hook for setting focus to editor areas (and widgets), and for checking if an area is currently focused
 */
export const useContextMenu = (): ContextMenuContextI => {
  const contextMenuContext = useContext(ContextMenuContext);

  return contextMenuContext;
};
